import React, { useState } from "react"
import * as Yup from "yup"
import { useFormik } from "formik"
import {
  Card,
  Stack,
  Button,
  Divider,
  TextField,
  Container,
  Typography,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress
} from "@mui/material"
import { Visibility, VisibilityOff } from "@mui/icons-material"
import GoogleIcon from "@mui/icons-material/Google"
import { createUserWithEmailAndPassword, sendEmailVerification, updateProfile } from "firebase/auth"
import { auth, db } from "@/config/firebase"
import { doc, serverTimestamp, setDoc } from "firebase/firestore"
import { isUndefined, omit } from "lodash"
import { useGoogleReCaptcha } from "react-google-recaptcha-v3"
import { Link } from "react-router-dom"
import { useGoogleAuth } from "@/features/auth/hooks/useGoogleAuth"
import { trackSignupConversion } from "@/shared/services/googleAds"

const SignUpSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Email is required"),
  password: Yup.string().min(8, "Password too short").required("Password is required"),
  full_name: Yup.string().required("Name is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password")], "Passwords must match")
    .required("Confirm password is required")
})

interface Props {
  onToggleSignUp: () => void
}

const SignUp: React.FC<Props> = ({ onToggleSignUp }) => {
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>()
  const { signInWithGoogle, loading: googleLoading } = useGoogleAuth()

  const { executeRecaptcha } = useGoogleReCaptcha()

  const handleTogglePasswordVisibility = () => {
    setShowPassword(prev => !prev)
  }

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      confirmPassword: "",
      full_name: ""
    },
    validationSchema: SignUpSchema,
    onSubmit: async values => {
      if (!executeRecaptcha) {
        setErrorMessage("reCAPTCHA not ready. Please try again.")
        return
      }
      const token = await executeRecaptcha("signup_submit")
      if (!token) {
        setErrorMessage("reCAPTCHA failed. Please retry.")
        return
      }

      try {
        setLoading(true)
        const data = await createUserWithEmailAndPassword(auth, values.email, values.password)
        await updateProfile(data.user, { displayName: values.full_name })

        const user = data.user
        await setDoc(doc(db, "users", user.uid), {
          ...omit(values, ["confirmPassword", "password"]),
          createdAt: serverTimestamp()
        })
        trackSignupConversion("email")

        const actionCodeSettings = {
          url: "https://listyourproduct.com",
          handleCodeInApp: true
        }
        await sendEmailVerification(data.user, actionCodeSettings)
      } catch (error) {
        const code = (error as any)?.code
        if (code === "auth/email-already-in-use") {
          setErrorMessage("This email is taken, please enter different email address.")
        } else {
          console.log((error as any)?.code, "failed")
        }
      } finally {
        setLoading(false)
      }
    }
  })

  const passwordsMatch =
    formik.values.password.length > 0 &&
    formik.values.confirmPassword.length > 0 &&
    formik.values.password === formik.values.confirmPassword

  return (
    <Container maxWidth={false} disableGutters sx={{ width: "100%" }}>
      <Card variant="outlined" sx={{ p: { xs: 3, sm: 3.5 }, mt: { xs: 2, sm: 2, md: 3 } }}>
        <Stack spacing={2.5} component="form" onSubmit={formik.handleSubmit}>
          <Typography variant="h5" component="h1" gutterBottom>
            Sign Up
          </Typography>
          <TextField
            fullWidth
            id="email"
            name="email"
            label="Email"
            size="small"
            margin="normal"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />
          <TextField
            fullWidth
            id="password"
            name="password"
            label="Password"
            type={showPassword ? "text" : "password"}
            size="small"
            margin="normal"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
          />
          <TextField
            fullWidth
            id="confirmPassword"
            name="confirmPassword"
            label="Confirm Password"
            type={showPassword ? "text" : "password"}
            size="small"
            margin="normal"
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
            helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleTogglePasswordVisibility} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <TextField
            fullWidth
            id="full_name"
            name="full_name"
            label="Name"
            size="small"
            margin="normal"
            value={formik.values.full_name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.full_name && Boolean(formik.errors.full_name)}
            helperText={formik.touched.full_name && formik.errors.full_name}
          />

          {!isUndefined(errorMessage) && (
            <Alert
              severity="warning"
              onClose={() => {
                setErrorMessage(undefined)
              }}>
              {errorMessage}
            </Alert>
          )}

          <Button
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            color="primary"
            variant="contained"
            fullWidth
            disabled={loading || !passwordsMatch}
            type="submit">
            Sign Up
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={googleLoading ? <CircularProgress size={18} color="inherit" /> : <GoogleIcon />}
            onClick={() => void signInWithGoogle()}
            disabled={googleLoading}>
            Sign up with Google
          </Button>
          <Divider sx={{ mt: 2, mb: 2 }} />
          <Button onClick={onToggleSignUp} color="secondary" variant="outlined">
            Login
          </Button>
          <Typography variant="caption" color="text.secondary" textAlign="center">
            By signing up, you agree to our{" "}
            <Typography component={Link} to="/terms" variant="caption">
              Terms of Use
            </Typography>{" "}
            and{" "}
            <Typography component={Link} to="/privacy" variant="caption">
              Privacy Policy
            </Typography>
            .
          </Typography>
        </Stack>
      </Card>
    </Container>
  )
}

export default SignUp
